import { TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';

import { usePagedTableFilters } from '../../foundation/hooks';
import {
  useClaimWorklistSampleMutation,
  useGetWorklistFilterOptionsQuery,
  useGetWorklistQuery,
} from '../api/variantApi';
import type {
  WorklistColumnFilters,
  WorklistSample,
  WorklistView,
} from './types';

const initialColumnFilters: WorklistColumnFilters = {
  sample_id: [],
  subject_external_id: [],
  indication: [],
  reporter_display_name: [],
  status: [],
  reviewer_display_name: [],
};

const PAGE_SIZE = 15;
// Lightweight query (no filters/sort) used purely to show the tab badge count
// for the tab that is not currently active.
const COUNT_ONLY_LIMIT = 1;

export const useWorklistTable = () => {
  const [view, setView] = useState<WorklistView>('pool');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>(
    undefined,
  );

  const resetPage = useCallback(() => setCurrentPage(1), []);

  const {
    columnFilters: sharedColumnFilters,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
    getActiveFiltersCount,
  } = usePagedTableFilters({ initialFilters: initialColumnFilters }, resetPage);
  const columnFilters = sharedColumnFilters as WorklistColumnFilters;

  const activeQueryParams = {
    view,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    search: appliedSearch.trim() || undefined,
    sortField,
    sortOrder,
    sample_id_filter: columnFilters.sample_id.length
      ? columnFilters.sample_id
      : undefined,
    subject_filter: columnFilters.subject_external_id.length
      ? columnFilters.subject_external_id
      : undefined,
    indication_filter: columnFilters.indication.length
      ? columnFilters.indication
      : undefined,
    reporter_filter: columnFilters.reporter_display_name.length
      ? columnFilters.reporter_display_name
      : undefined,
    status_filter: columnFilters.status?.length
      ? columnFilters.status
      : undefined,
  };

  const { data: activeData, isFetching } =
    useGetWorklistQuery(activeQueryParams);

  // Badge count for the tab that is currently NOT displayed, fetched without
  // filters so the count always reflects everything available in that tab.
  const otherView: WorklistView = view === 'pool' ? 'mine' : 'pool';
  const { data: otherCountData } = useGetWorklistQuery({
    view: otherView,
    limit: COUNT_ONLY_LIMIT,
    offset: 0,
  });

  const poolCount =
    view === 'pool'
      ? (activeData?.total_result_count ?? 0)
      : (otherCountData?.total_result_count ?? 0);
  const mineCount =
    view === 'mine'
      ? (activeData?.total_result_count ?? 0)
      : (otherCountData?.total_result_count ?? 0);

  const { data: filterOptionsData } = useGetWorklistFilterOptionsQuery();

  const [claimSample, { isLoading: isClaiming }] =
    useClaimWorklistSampleMutation();

  const samples: WorklistSample[] = activeData?.results ?? [];
  const totalResultCount = activeData?.total_result_count ?? 0;

  const handleSearch = useCallback(() => {
    resetPage();
    setAppliedSearch(searchInput);
  }, [searchInput, resetPage]);

  const handleSetView = useCallback(
    (nextView: WorklistView) => {
      setView(nextView);
      resetPage();
    },
    [resetPage],
  );

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter: SorterResult<WorklistSample> | SorterResult<WorklistSample>[],
    ) => {
      const activeSorter = Array.isArray(sorter)
        ? sorter.find((item) => item.order)
        : sorter;

      setSortField(
        activeSorter?.order ? (activeSorter.columnKey as string) : undefined,
      );
      setSortOrder(
        activeSorter?.order === 'ascend' || activeSorter?.order === 'descend'
          ? activeSorter.order
          : undefined,
      );

      if (pagination.current) {
        setCurrentPage(pagination.current);
      }
    },
    [],
  );

  const handleClaim = useCallback(
    async (sample: WorklistSample) => {
      await claimSample({ sample_id: sample.id }).unwrap();
    },
    [claimSample],
  );

  return {
    view,
    currentPage,
    searchInput,
    setSearchInput,
    appliedSearch,
    setAppliedSearch,
    handleSearch,
    setView: handleSetView,
    columnFilters,
    filterOptionsData,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
    getActiveFiltersCount,
    handleTableChange,
    samples,
    totalResultCount,
    isFetching,
    poolCount,
    mineCount,
    handleClaim,
    isClaiming,
    pageSize: PAGE_SIZE,
  };
};
